import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { message } from 'antd';

import useTranslation from 'customHooks/translate';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { ForgotPasswordCustomError } from 'types';

import ForgotPasswordForm from '../components/forms/forgot-password-form';
import { passwordResetLink } from '../services/auth';
import HeaderSecondary from '../components/Header/headerSecondary';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const translation = useTranslation();
  const [, setDateInStorage] = useLocalStorage('date', '');
  const [, setEmailInStorage] = useLocalStorage('email', '');

  const [emailError, setEmailError] = useState<string | null>(null);

  const {
    mutate: passwordResetHandler,
    isLoading,
    isSuccess,
  } = useMutation(passwordResetLink, {
    onSuccess({ data }, { email }) {
      setEmailInStorage(email);
      setDateInStorage(data);
      navigate('/check-email', { replace: true, state: { date: data, email } });
    },

    onError(error: ForgotPasswordCustomError) {
      message.error(translation.ErrorMessage);

      const emailErrorMessage =
        error.response?.data?.errors?.Email?.[0] || null;

      const formattedEmailError = emailErrorMessage
        ? emailErrorMessage.charAt(0).toUpperCase() + emailErrorMessage.slice(1)
        : null;

      setEmailError(formattedEmailError);

      const generalErrorMessage = error.response?.data?.Message || null;
      if (generalErrorMessage) {
        message.error(generalErrorMessage);
      }
    },
  });

  return (
    <section className="box-border grid h-screen grid-cols-1 gap-[60px] overflow-hidden px-[24px] py-[24px]  md:pr-[24px] lg:grid-cols-2 xl:grid-cols-3 xl:gap-[120px] xl:pl-[156px]">
      <div className="col-span-1 flex flex-col gap-[96px]">
        <HeaderSecondary />
        <div>
          {isSuccess ? (
            <h1 className="text-4xl font-bold text-charleston-green md:text-4xl">
              {translation.forgotPassword.WeSent}
            </h1>
          ) : (
            <div className="flex flex-col gap-[24px]">
              <div className="flex flex-col gap-[12px]">
                <h1 className="text-2xl font-bold text-charleston-green md:text-4xl">
                  {translation.forgotPassword.ForgotText}
                </h1>
                <span className="text-charleston-green">
                  {translation.forgotPassword.ForgotSubText}
                </span>
              </div>
              <ForgotPasswordForm
                isLoading={isLoading}
                passwordResetHandler={passwordResetHandler}
                emailError={emailError}
              />
            </div>
          )}
        </div>
      </div>

      <div className="cols-span-1 hidden h-full overflow-hidden rounded-[24px] lg:block xl:col-span-2">
        <img
          src="assets/login-cover.svg"
          alt="login-cover"
          className="h-full w-full object-cover"
        />
      </div>
    </section>
  );
};

export default ForgotPassword;
